/**
 * Message handling for TwoPoint Widget
 */

import type { TwoPointWidgetConfig, HeightUpdateMessage, ScrollToTopMessage } from './types';
import { debugLog } from './utils';

/**
 * Handle height update message with Safari double RAF fix
 */
export const handleHeightUpdate = (
  iframe: HTMLIFrameElement,
  message: HeightUpdateMessage,
  config: Required<TwoPointWidgetConfig>
): void => {
  const { debugMode, onHeightUpdate } = config;
  const { height, widgetId } = message;

  debugLog(debugMode, 'Received height:', height, 'for', widgetId);

  // Safari fix: double requestAnimationFrame + forced reflows
  const heightPx = height + 'px';
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      iframe.style.height = heightPx;
      iframe.style.minHeight = heightPx;
      iframe.style.maxHeight = 'none';

      // Force reflow (Safari compatibility)
      void iframe.offsetHeight;
      void iframe.getBoundingClientRect();
    });
  });

  if (onHeightUpdate) {
    onHeightUpdate(widgetId || iframe.id, height);
  }
};

/**
 * Handle scroll to top request
 */
export const handleScrollToTop = (
  iframe: HTMLIFrameElement,
  message: ScrollToTopMessage,
  config: Required<TwoPointWidgetConfig>
): void => {
  const { debugMode, onScrollRequest } = config;
  const { widgetId } = message;

  debugLog(debugMode, 'Scroll to top for', widgetId);
  iframe.scrollIntoView({ behavior: 'smooth', block: 'start' });

  if (onScrollRequest) {
    onScrollRequest(widgetId || iframe.id);
  }
};

/**
 * Create message event handler
 */
export const createMessageHandler = (
  config: Required<TwoPointWidgetConfig>
) => {
  return (event: MessageEvent): void => {
    // Validate message has required properties
    if (!event.data || typeof event.data !== 'object' || !event.data.type) {
      return;
    }

    // Find iframe by widgetId
    const widgetId = event.data.widgetId;
    if (!widgetId) return;

    const iframe = document.getElementById(widgetId) as HTMLIFrameElement | null;
    if (!iframe || iframe.tagName !== 'IFRAME') return;

    // Validate origin
    const expectedOrigin = new URL(iframe.src).origin;
    if (event.origin !== expectedOrigin) return;

    // Handle message types
    switch (event.data.type) {
      case 'TWIPOINT_IFRAME_HEIGHT':
        handleHeightUpdate(iframe, event.data as HeightUpdateMessage, config);
        break;
      case 'TWIPOINT_SCROLL_TO_TOP':
        handleScrollToTop(iframe, event.data as ScrollToTopMessage, config);
        break;
    }
  };
};
